/**
 * warp-map.js — window.WarpMap / module.exports
 *
 * КРИВАЯ натяжения времени: сколько входного звука приходится на каждый
 * выходной сэмпл, когда это отношение МЕНЯЕТСЯ по ходу записи.
 *
 * Зачем. Тайм-стретч у нас умеет один коэффициент на весь файл
 * (SignalsmithWorkerEngine.shift({rate})). Этого хватает, чтобы «ускорить в
 * полтора раза», и НЕ хватает, чтобы положить живую партию на живую запись:
 * человек играет с уводом темпа, и постоянный коэффициент, подобранный по
 * среднему, расходится к концу. Документация bpm-detector.js называет цену
 * этого прямо: «4 % at 124 BPM is 7.7 s over three minutes».
 *
 * Что это. Пары «время входа → время выхода», между которыми время идёт
 * равномерно. Ровно то, что выдаёт сопоставление долей: доля k партии звучит в
 * b_k, доля k опоры звучит в a_k, значит b_k обязано оказаться в a_k.
 *
 *     var plan = WarpMap.build([[0,0],[1.02,1.00]], { sampleRate: 44100, inFrames: N });
 *     WarpMap.inputAt(plan, outSample) -> позиция во входе (сэмплы, дробная)
 *
 * ⚠️ Модуль НИЧЕГО не решает за вызывающего. Кривая, которая требует тянуть
 * быстрее MAX_RATE или медленнее MIN_RATE, ОТВЕРГАЕТСЯ с причиной, а не
 * поджимается молча: такая кривая почти всегда означает не «человек так
 * сыграл», а ошибку в сетке долей, и поджатая она звучит как рваная плёнка.
 *
 * ⚠️ Чистая математика, без DOM и без Web Audio: те же числа считаются в Node
 * (tests/js/warp-map.test.mjs) и внутри воркера, поэтому кривая проверяется
 * без браузера и без wasm.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.WarpMap = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* Сколько входного времени приходится на единицу выходного. rate > 1 —
       вход бежит быстрее выхода, то есть партию РАСТЯГИВАЕМ. */
    var MIN_RATE = 0.25;
    var MAX_RATE = 4;
    /* Две доли не могут стоять в одной точке: нулевой интервал даёт деление на
       ноль, а интервал короче этого — коэффициент, который ничего не значит. */
    var MIN_SEG_S = 1e-3;
    /* Насколько коэффициент головы может отличаться от соседнего, когда она не
       помещается и его приходится подбирать. 10 % — с запасом больше любого
       живого увода темпа и заметно меньше того, что слышно как рывок. */
    var HEAD_TOL = 1.1;

    function isNum(v) { return typeof v === 'number' && isFinite(v); }

    /**
     * Годится ли набор пар как кривая. Возвращает null или ПРИЧИНУ отказа —
     * строку, по которой вызывающий спускается на ступень ниже (постоянный
     * коэффициент, затем только смещение, затем руками).
     */
    function validate(pairs) {
        if (!Array.isArray(pairs) || pairs.length < 2) return 'need_two_anchors';
        for (var i = 0; i < pairs.length; i++) {
            var p = pairs[i];
            if (!Array.isArray(p) || p.length < 2 || !isNum(p[0]) || !isNum(p[1])) return 'bad_anchor';
            if (p[0] < 0 || p[1] < 0) return 'negative_time';
            if (i > 0) {
                var dIn = p[0] - pairs[i - 1][0];
                var dOut = p[1] - pairs[i - 1][1];
                if (dIn < MIN_SEG_S || dOut < MIN_SEG_S) return 'not_increasing';
                var r = dIn / dOut;
                if (r < MIN_RATE || r > MAX_RATE) return 'rate_out_of_range';
            }
        }
        return null;
    }

    /**
     * Пары (в секундах) → план в СЭМПЛАХ.
     *
     * Голова и хвост. Кривая описывает участок между первой и последней долей,
     * а у партии есть звук до первой доли и после последней. Он идёт с
     * коэффициентом ближайшего участка — единственное продолжение, которое не
     * рвёт темп на границе. Голова, которая при этом не помещается, получает
     * свой коэффициент, но лишь в пределах HEAD_TOL — см. разбор на месте.
     */
    function build(pairs, opts) {
        opts = opts || {};
        var sr = opts.sampleRate;
        if (!isNum(sr) || sr <= 0) return { error: 'bad_sample_rate' };
        var bad = validate(pairs);
        if (bad) return { error: bad };

        var n = pairs.length;
        var rate0 = (pairs[1][0] - pairs[0][0]) / (pairs[1][1] - pairs[0][1]);
        var rateN = (pairs[n - 1][0] - pairs[n - 2][0]) / (pairs[n - 1][1] - pairs[n - 2][1]);

        /* Голова — звук до первой доли. Обычно она идёт коэффициентом первого
           участка и просто начинается позже: артефакта нет.

           Но бывает, что при этом коэффициенте она не помещается — первая доля
           партии дальше от её начала, чем доля опоры от своего. Тогда у головы
           СВОЙ коэффициент, тот единственный, при котором она встаёт впритык.
           ⚠️ Принимается он только если близок к соседнему: разрыв в полпроцента
           (а такой даёт любой живой увод темпа — замер стенда: 0.6 мс на
           двадцатисекундной партии) неслышен, а разрыв в полтора раза означает
           не «человек так сыграл», а партию, поставленную не туда. Мять ради
           этого звук нельзя — надо сказать, чтобы её подвинули. */
        var outAtZero = pairs[0][1] - pairs[0][0] / rate0;
        if (outAtZero < 0) {
            if (pairs[0][1] <= MIN_SEG_S) return { error: 'head_does_not_fit' };
            var headRate = pairs[0][0] / pairs[0][1];
            var off = headRate > rate0 ? headRate / rate0 : rate0 / headRate;
            if (off > HEAD_TOL) return { error: 'head_does_not_fit' };
            if (headRate < MIN_RATE || headRate > MAX_RATE) return { error: 'rate_out_of_range' };
            outAtZero = 0;
        }

        var pts = [[0, outAtZero]];
        for (var i = 0; i < n; i++) {
            if (pairs[i][0] > pts[pts.length - 1][0] + MIN_SEG_S / 2) pts.push([pairs[i][0], pairs[i][1]]);
        }
        if (pts.length < 2) return { error: 'need_two_anchors' };

        /* Хвост: сколько входа осталось после последней доли и куда он ляжет. */
        var inFrames = isNum(opts.inFrames) ? opts.inFrames : null;
        var last = pts[pts.length - 1];
        if (inFrames !== null) {
            var inEndSec = inFrames / sr;
            if (inEndSec > last[0] + MIN_SEG_S) {
                pts.push([inEndSec, last[1] + (inEndSec - last[0]) / rateN]);
            }
        }

        var flat = new Float64Array(pts.length * 2);
        var minR = Infinity, maxR = 0;
        for (var k = 0; k < pts.length; k++) {
            flat[k * 2] = pts[k][1] * sr;      // выход, сэмплы
            flat[k * 2 + 1] = pts[k][0] * sr;  // вход, сэмплы
            if (k > 0) {
                var dOut = flat[k * 2] - flat[(k - 1) * 2];
                var dIn = flat[k * 2 + 1] - flat[(k - 1) * 2 + 1];
                if (dOut <= 0 || dIn <= 0) return { error: 'not_increasing' };
                var rr = dIn / dOut;
                if (rr < minR) minR = rr;
                if (rr > maxR) maxR = rr;
            }
        }
        if (minR < MIN_RATE || maxR > MAX_RATE) return { error: 'rate_out_of_range' };

        return {
            pts: flat,
            outFrames: Math.max(1, Math.round(flat[flat.length - 2])),
            minRate: minR,
            maxRate: maxR,
            rate0: rate0,
            sampleRate: sr
        };
    }

    /**
     * Позиция во входе для заданного выходного сэмпла. Вне кривой — по
     * коэффициенту крайнего участка; ниже нуля не опускается, потому что
     * отрицательного входа не бывает.
     */
    function inputAt(plan, outSample) {
        var p = plan.pts, m = p.length / 2;
        if (outSample <= p[0]) {
            var r0 = (p[3] - p[1]) / (p[2] - p[0]);
            var v = p[1] + (outSample - p[0]) * r0;
            return v > 0 ? v : 0;
        }
        var lastOut = p[(m - 1) * 2], lastIn = p[(m - 1) * 2 + 1];
        if (outSample >= lastOut) {
            var rN = (lastIn - p[(m - 2) * 2 + 1]) / (lastOut - p[(m - 2) * 2]);
            return lastIn + (outSample - lastOut) * rN;
        }
        /* Двоичный поиск участка: у минутной партии долей под сотню. */
        var lo = 0, hi = m - 1;
        while (hi - lo > 1) {
            var mid = (lo + hi) >> 1;
            if (p[mid * 2] <= outSample) lo = mid; else hi = mid;
        }
        var oA = p[lo * 2], iA = p[lo * 2 + 1];
        var oB = p[hi * 2], iB = p[hi * 2 + 1];
        return iA + (outSample - oA) * (iB - iA) / (oB - oA);
    }

    return {
        validate: validate,
        build: build,
        inputAt: inputAt,
        MIN_RATE: MIN_RATE,
        MAX_RATE: MAX_RATE
    };
});
